import { useCallback, useEffect, useRef, useState } from 'react'
import { useTour } from './use-tour'
import { TourTooltip } from './tour-tooltip'

interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

const PADDING = 8
const TOOLTIP_GAP = 12

/**
 * Spotlight overlay that highlights a target element and positions
 * a tooltip card adjacent to it.
 */
export function TourOverlay() {
  const { currentStep, currentStepIndex } = useTour()
  const [rect, setRect] = useState<TargetRect | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const frameRef = useRef<number>(0)

  const measure = useCallback(() => {
    if (!currentStep?.targetSelector) {
      setRect(null)
      return
    }
    const el = document.querySelector(currentStep.targetSelector)
    if (!el) {
      setRect(null)
      return
    }
    const r = el.getBoundingClientRect()
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    })
  }, [currentStep])

  useEffect(() => {
    if (!currentStep?.targetSelector) {
      setRect(null)
      return
    }

    let attempts = 0
    const maxAttempts = 30

    function tryFind() {
      const el = document.querySelector(currentStep!.targetSelector!)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          measure()
          observerRef.current = new ResizeObserver(measure)
          observerRef.current.observe(el)
        }, 350)
        return
      }
      attempts++
      if (attempts < maxAttempts) {
        frameRef.current = window.setTimeout(tryFind, 100) as unknown as number
      }
    }

    tryFind()

    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)

    return () => {
      observerRef.current?.disconnect()
      clearTimeout(frameRef.current)
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [currentStep, currentStepIndex, measure])

  if (currentStepIndex === null || !currentStep?.targetSelector || !rect) {
    return null
  }

  const placement = currentStep.placement
  let tooltipTop = 0
  let tooltipLeft = 0

  if (placement === 'bottom') {
    tooltipTop = rect.top + rect.height + TOOLTIP_GAP
    tooltipLeft = rect.left
  } else if (placement === 'top') {
    tooltipTop = rect.top - TOOLTIP_GAP
    tooltipLeft = rect.left
  } else if (placement === 'right') {
    tooltipTop = rect.top
    tooltipLeft = rect.left + rect.width + TOOLTIP_GAP
  } else {
    tooltipTop = rect.top
    tooltipLeft = rect.left - 320 - TOOLTIP_GAP
  }

  tooltipLeft = Math.max(16, Math.min(tooltipLeft, window.innerWidth - 340))
  tooltipTop = Math.max(16, Math.min(tooltipTop, window.innerHeight - 240))

  return (
    <div className="pointer-events-none fixed inset-0 z-[52]" aria-hidden="true">
      <div
        className="pointer-events-auto absolute rounded-[8px]"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
        }}
      />
      <div
        className="pointer-events-auto absolute"
        style={{
          top: placement === 'top' ? undefined : tooltipTop,
          bottom: placement === 'top' ? window.innerHeight - tooltipTop : undefined,
          left: tooltipLeft,
        }}
      >
        <TourTooltip placement={placement} />
      </div>
    </div>
  )
}
